import React, { Component, useState, useEffect } from 'react';
import { Container, Draggable } from 'react-smooth-dnd';
// import { FixedSizeList as VirtualList } from 'react-window';
import { isEqual, cloneDeep } from 'lodash';
import envConfig from '@nile/home/src/Config/env';
import styled from 'styled-components';
import Loader from '../Utils/Loader';

/**
 * This is the individual list of the super board
 */
class List extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      currentPage: 1,
      pageEnd: false,
      isPageLoading: false,
      listItems: null
    };
  }

  componentDidMount() {
    this.updateList();
  }

  /**
   *
   * When we switch between different boards, we need to refresh the lists.
   * @param {object} prevProps
   * @param {object} prevState
   * @public
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevProps.list?.id !== this.props.list?.id) {
      this.updateList();
    }
    const currentListIdx = this.props?.board?.findIndex(
      (el) => el.id === this.props.list?.id
    );
    if (
      this.props?.board?.[currentListIdx]?.items &&
      !isEqual(this.state.listItems, this.props?.board?.[currentListIdx]?.items)
    ) {
      this.setState({ listItems: this.props.board[currentListIdx].items });
    }
    if (this.props.refreshLists) {
      this.refreshList();
    }
  }

  /**
   * This method is called when we want to fetch data for each list individually
   * @public
   */
  refetchList = (list) => {
    const { getListItems, dispatch } = this.props;
    getListItems({ list })
      .then((res) => {
        const { items, params } = res;
        dispatch({
          type: 'updateList',
          list: list,
          params: params,
          items: items || []
        });
      })
      .catch((err) => {});
  };

  /**
   * This method is called when we want to fetch data for each list individually
   * @public
   */
  updateList = (callback) => {
    const { getListItems, list, dispatch } = this.props;
    this.setState({ isLoading: true });
    getListItems({ list })
      .then((res) => {
        const { items, params } = res;
        this.setState({ listItems: items || [] });
        dispatch({
          type: 'updateList',
          list: list,
          params: params,
          items: items || []
        });
        if (callback) {
          callback(res);
        }
        this.setState({ isLoading: false });
      })
      .catch((err) => {
        this.setState({ listItems: this.state.listItems || [] });
        dispatch({
          type: 'updateList',
          list: list,
          items: this.state.listItems || []
        });
        this.setState({ isLoading: false });
      });
  };

  /**
   * On reaching the end of the list, we call the parent to check if there is pagination available
   * @param {object} evt
   * @public
   */
  handleScroll = (evt) => {
    const { list, dispatch, board } = this.props;
    const {
      isLoading,
      currentPage,
      pageEnd,
      isPageLoading,
      listItems
    } = this.state;
    // console.log('scrolling');
    const node = evt.target;
    const elemScrollPosition =
      node.scrollHeight - node.scrollTop - node.clientHeight;
    // console.log(elemScrollPosition);
    const { onLaneScroll } = this.props;
    if (elemScrollPosition < 1 && onLaneScroll && !isPageLoading && !pageEnd) {
      this.setState({ isPageLoading: true });
      onLaneScroll(this.state.currentPage + 1, list?.id, board)
        .then((moreCards) => {
          if ((moreCards || []).length > 0) {
            // this.setState({
            //   listItems: this.state.listItems.concat(moreCards),
            // });
            this.setState({ currentPage: currentPage + 1 });
            dispatch({
              type: 'updateList',
              list: list,
              items: listItems.concat(moreCards) || []
            });
          } else {
            this.setState({ pageEnd: true });
          }
          this.setState({ isPageLoading: false });
        })
        .catch((err) => {
          this.setState({ isPageLoading: false });
        });
    }
  };

  /**
   * Gets called when a card is dropped. The payload we include while dragging will be used here.
   * @param {object} list
   * @param {object} meta
   * @public
   */
  onCardDrop = (list, meta) => {
    const { dispatch, board } = this.props;
    if (meta.addedIndex !== null) {
      const sourceListId = meta.payload.list?.id;
      const targetListId = list?.id;
      dispatch({
        type: 'moveCard',
        sourceListId: meta.payload.list?.id,
        item: meta.payload.item,
        targetListId: list?.id,
        addedIndex: meta.addedIndex,
        removedIndex: meta.removedIndex
      });
      const newState = cloneDeep(board);
      const sourceList = newState.findIndex((el) => el.id === sourceListId);
      const targetList = newState.findIndex((el) => el.id === targetListId);
      if (sourceListId === targetListId) {
        newState[targetList].items.splice(meta.removedIndex, 1);
        newState[targetList].items.splice(
          meta.addedIndex,
          0,
          meta.payload.item
        );
      } else {
        newState[targetList].items.splice(
          meta.addedIndex,
          0,
          meta.payload.item
        );
        newState[sourceList] = {
          ...newState[sourceList],
          items: newState[sourceList].items.filter((el) => {
            return el.id !== meta.payload.item.id;
          })
        };
      }
      if (this.props.onCardDrop) {
        this.props.onCardDrop(
          sourceListId,
          targetListId,
          meta.payload.item,
          board,
          dispatch,
          newState
        );
      }
    }
  };

  /**
   * This method gets triggered when we drag a card from any list?.
   * The data we pass in this method will be used when the card is dropped in another list
   * @param {object} li
   * @param {Number} idx
   * @public
   */
  getCardPayload = (li, idx) => {
    return { list: li, idx: idx, item: li.items[idx] };
  };

  refreshList = () => {
    this.updateList();
    this.props.refreshComplete();
  };

  /*
   * This method is called when we want to reset to initial state
   */
  resetListState() {
    this.setState((prevState) => ({
      ...prevState,
      currentPage: 1,
      pageEnd: false,
      isPageLoading: false
    }));
  }

  checkIsCardDragRestricted(data) {
    if (this.props.restrictCardDragCheck) {
      if (typeof this.props.restrictCardDragCheck !== 'function') {
        throw new Error('restrictCardDragCheck - expected a function');
      }
      return this.props.restrictCardDragCheck(data);
    }
    return false;
  }

  render() {
    const {
      list,
      getListItems,
      components,
      actions,
      pinnedColumn = false
    } = this.props;
    const {
      isLoading,
      currentPage,
      pageEnd,
      isPageLoading,
      listItems
    } = this.state;
    return (
      <Draggable key={list?.id}>
        <ListHolder className={`super-list ${list?.className || ''}`}>
          <div className="column-drag-handle">
            <components.ListHeader
              list={list}
              actions={actions}
              pinnedColumn={pinnedColumn}
              updateList={this.updateList}
            />
          </div>
          {isLoading ? (
            <Loader />
          ) : (
            <ListItems
              className="super-list-items"
              ref={(node) => {
                if (node) {
                  node.addEventListener('scroll', this.handleScroll);
                }
              }}
              hasItems={listItems && listItems.length > 0}
            >
              <Container
                groupName="col"
                onDragStart={(e) => {
                  //   console.log("drag started", e);
                }}
                onDragEnd={(e) => {
                  //   console.log("drag end", e);
                }}
                onDrop={(e) => {
                  //   console.log("dropped dude!!", list?.id);
                  this.onCardDrop(list, e);
                }}
                getChildPayload={(index) => this.getCardPayload(list, index)}
                dragClass="card-ghost"
                dropClass="card-ghost-drop"
                onDragEnter={() => {
                  //   console.log("drag enter:", list?.id);
                }}
                onDragLeave={() => {
                  //   console.log("drag leave:", list?.id);
                }}
                onDropReady={(p) => {
                  // console.log('Drop ready: ', p)
                }}
                dropPlaceholder={{
                  animationDuration: 150,
                  showOnTop: true,
                  className: 'drop-preview'
                }}
                dropPlaceholderAnimationDuration={200}
              >
                {/* <VirtualList
                  className="List"
                  height={700}
                  itemCount={listItems.length}
                  itemSize={100}
                  width={300}
                >
                  {(prop) => {
                    return (
                      <Draggable key={listItems[prop.index].id}>
                        <ListItem>{listItems[prop.index].label}</ListItem>
                      </Draggable>
                    );
                  }}
                </VirtualList> */}
                {listItems.map((li) => {
                  const isNotDraggable = this.checkIsCardDragRestricted({
                    cardData: li,
                    boardData: this.props.board
                  });
                  return (
                    <div
                      key={li.id}
                      className={
                        isNotDraggable
                          ? 'flex jctr actr cursor-not-allowed'
                          : 'smooth-dnd-draggable-wrapper'
                      }
                    >
                      <components.Card
                        item={li}
                        list={list}
                        board={this.props.board}
                        dispatch={this.props.dispatch}
                        updateList={this.updateList}
                        isNotDraggable={isNotDraggable}
                        refetchList={this.refetchList}
                      />
                    </div>
                  );
                })}
                {isPageLoading && (
                  <LoaderContainer>
                    <Loader />
                  </LoaderContainer>
                )}
              </Container>
            </ListItems>
          )}
        </ListHolder>
      </Draggable>
    );
  }
}

const ListHolder = styled.div`
  height: 80vh;
  width: 400px;
  margin-right: 16px;
`;

const ListItems = styled.div`
  height: 100%;
  overflow: auto;
  ${(props) =>
    !props.hasItems &&
    `
    background-image: url(${envConfig.imageRoot}svg/empty-kanban-card.svg);
    background-repeat: no-repeat;
    background-position: top;
    background-size: contain;
  `}
  .smooth-dnd-container.vertical > .smooth-dnd-draggable-wrapper {
    display: flex !important;
    justify-content: center !important;
  }
  .card-ghost {
    transform: rotate(3deg);
  }
  .drop-preview {
    max-width: 280px;
    margin: 0 auto 20px;
    background: rgba(234, 234, 248, 0.5);
    opacity: 0.5;
    border: 1px dashed #5f8bff;
    border-radius: 5px;
  }
`;

const LoaderContainer = styled.div`
  width: 100%;
  height: 100px;
`;

export default List;
